import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import Layout, { PageTitle } from "@/components/Layout";
import { NAV_BENDAHARA } from "@/lib/nav";
import { Plus, Pencil, Trash2, X, Save } from "lucide-react";
import { toast } from "sonner";

export default function BendaharaPemberitahuan() {
  const [items, setItems] = useState([]);
  const [siswa, setSiswa] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null); // {id, siswa_id, isi}
  const [form, setForm] = useState({ siswa_id: "", isi: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: p }, { data: s }] = await Promise.all([
        api.get("/pemberitahuan-bendahara"),
        api.get("/siswa"),
      ]);
      setItems(p);
      setSiswa((s || []).filter((x) => x.status === "aktif"));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setForm({ siswa_id: "", isi: "" }); setShowForm(true); };
  const openEdit = (it) => { setEditing(it); setForm({ siswa_id: it.siswa_id, isi: it.isi }); setShowForm(true); };
  const closeForm = () => { setShowForm(false); setEditing(null); setForm({ siswa_id: "", isi: "" }); };

  const submit = async (e) => {
    e.preventDefault();
    if (!editing && !form.siswa_id) return toast.error("Pilih siswa terlebih dahulu");
    if (!form.isi.trim()) return toast.error("Isi pemberitahuan tidak boleh kosong");
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/pemberitahuan-bendahara/${editing.id}`, { isi: form.isi });
        toast.success("Pemberitahuan diperbarui");
      } else {
        await api.post("/pemberitahuan-bendahara", { siswa_id: form.siswa_id, isi: form.isi });
        toast.success("Pemberitahuan tersimpan & ditampilkan di login Ortu");
      }
      closeForm(); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Hapus pemberitahuan ini?\nRunning text di login Ortu akan langsung hilang.")) return;
    try {
      await api.delete(`/pemberitahuan-bendahara/${id}`);
      toast.success("Dihapus");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Layout nav={NAV_BENDAHARA}>
      <PageTitle title="Pemberitahuan Ortu" action={
        <button onClick={openCreate} className="pp-btn-primary" data-testid="pemb-new">
          <Plus className="w-4 h-4" /> Input Pemberitahuan
        </button>
      } />

      <section className="pp-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-[800px] w-full text-sm" data-testid="pemb-table">
            <thead><tr className="pp-table-header">
              <th className="text-left px-5 py-3">Tanggal</th>
              <th className="text-left px-5 py-3 w-56">Nama Siswa</th>
              <th className="text-left px-5 py-3">Isi Pemberitahuan</th>
              <th className="text-right px-5 py-3 w-40">Aksi</th>
            </tr></thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={4} className="text-center py-10 text-slate-400 italic">Memuat…</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={4} className="text-center py-10 text-slate-400 italic">Belum ada pemberitahuan.</td></tr>
              ) : items.map((it) => (
                <tr key={it.id} className="pp-row" data-testid={`pemb-row-${it.id}`}>
                  <td className="px-5 py-3 font-mono text-xs">{(it.created_at || "").slice(0, 10)}</td>
                  <td className="px-5 py-3 font-medium">{it.nama_siswa}</td>
                  <td className="px-5 py-3 text-slate-700">{it.isi}</td>
                  <td className="px-5 py-3 text-right">
                    <div className="inline-flex gap-1">
                      <button onClick={() => openEdit(it)} className="pp-btn-ghost text-xs" data-testid={`pemb-edit-${it.id}`}>
                        <Pencil className="w-3.5 h-3.5" /> Edit
                      </button>
                      <button onClick={() => del(it.id)} className="pp-btn-danger" data-testid={`pemb-del-${it.id}`}>
                        <Trash2 className="w-3.5 h-3.5" /> Hapus
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="pemb-modal">
          <div className="bg-white rounded-lg w-full max-w-lg">
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
              <h4 className="font-semibold">{editing ? "Edit" : "Input"} Pemberitahuan</h4>
              <button onClick={closeForm} className="text-slate-500 hover:text-slate-900"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={submit} className="p-5 space-y-4">
              <div>
                <label className="pp-label">Nama Siswa (Aktif) *</label>
                <select required disabled={!!editing} className="pp-input" value={form.siswa_id} onChange={(e) => setForm({ ...form, siswa_id: e.target.value })} data-testid="pemb-siswa">
                  <option value="">— Pilih Siswa —</option>
                  {siswa.map((s) => <option key={s.id} value={s.id}>{s.nama} {s.nis ? `(${s.nis})` : ""}</option>)}
                </select>
                {editing && <p className="text-xs text-slate-500 mt-1">Nama siswa tidak dapat diubah saat edit.</p>}
              </div>
              <div>
                <label className="pp-label">Tulis Pemberitahuan *</label>
                <textarea required rows={4} className="pp-input" placeholder="Contoh: Ananda belum melunasi SPP bulan Agustus, mohon segera diselesaikan." value={form.isi} onChange={(e) => setForm({ ...form, isi: e.target.value })} data-testid="pemb-isi" />
                <p className="text-xs text-slate-500 mt-1">Pesan akan tampil sebagai running text merah di atas semua halaman login Orang Tua siswa terkait.</p>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={closeForm} className="pp-btn-secondary">Batal</button>
                <button type="submit" disabled={saving} className="pp-btn-primary" data-testid="pemb-save">
                  <Save className="w-4 h-4" /> {saving ? "Menyimpan…" : "Simpan"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}
